/**
 * The per-device "removed from this device" list — ONE copy of the key and ONE reader.
 *
 * The Templates page owns the gesture ("Remove from device": the cloud copy is kept, the local
 * record + bakes + case image are deleted, and the row keeps a "Not on this device" state per
 * D-28). The Mock-ups page has to READ the same list, because its cloud reconcile would
 * otherwise pull every deliberately-removed template straight back on the next mount.
 *
 * A second copy of the key string is exactly how `LOCAL_TEMPLATE_STORAGE` describes a contract
 * going quietly out of date, so both surfaces import this instead of spelling it again.
 */

/** localStorage key holding a JSON array of template ids removed from THIS device. */
export const HIDDEN_LOCAL_KEY = "genz.templates.hiddenLocal";

/** The ids removed from this device. Empty when storage is unavailable or unreadable. */
export function readHiddenLocalIds(): Set<string> {
  if (typeof localStorage === "undefined") return new Set();
  try {
    const raw = JSON.parse(localStorage.getItem(HIDDEN_LOCAL_KEY) ?? "[]") as unknown;
    return new Set(Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : []);
  } catch {
    return new Set(); // corrupt entry → behave as "nothing removed", never crash a page load
  }
}

/** Persist the set (the Templates page's writer). No-op without localStorage. */
export function writeHiddenLocalIds(ids: Iterable<string>): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(HIDDEN_LOCAL_KEY, JSON.stringify([...ids]));
}
